import { PropsWithChildren, useMemo } from 'react';

import { Button, Stack, WithRef } from '@cast/design-system';
import {
  EvictorAdvancedConfig,
  EvictorConfig,
} from '@cast/types-workload-eviction';
import { makeValidateSync } from '@cast/utils';

import { PageFormFooter } from '@cast/split-me/components/form-layouts';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { RffForm, RffFormRef, RffSwitch } from '@cast/shared-rff-ui';

import { validationSchema } from '../_validation';

import { AdvancedConfigurations } from './AdvancedConfigurations';
import { Configuration } from './Configuration';
import { EvictorSettingsHeader } from './EvictorSettingsHeader';
import { FormValues } from '../_types';
import {
  isEvictorConfigurable,
  mapConfigToFormValues,
  mapFormValuesToConfig,
} from '../_utils';

import { useAbility } from '@cast/shared-ability-data-access';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { EvictorSettingsAlert } from './EvictorSettingsAlert';

const validate = makeValidateSync(validationSchema);

const Layout = ({
  children,
  testId,
}: PropsWithChildren<{ testId?: string }>) => {
  return (
    <Stack gap={24} data-testid={testId}>
      {children}
    </Stack>
  );
};

type Props = WithRef<
  RffFormRef<FormValues>,
  {
    evictorConfig: EvictorConfig;
    advancedConfig: EvictorAdvancedConfig;
    clusterAgentVersion: string | undefined;
    isWoopInstalled: boolean;
    isPending: boolean;
    onSubmit: (args: {
      config: EvictorConfig;
      advancedConfig: EvictorAdvancedConfig;
    }) => void;
  }
>;

export const EvictorSettingsForm = ({
  evictorConfig,
  advancedConfig,
  clusterAgentVersion,
  isWoopInstalled,
  isPending,
  onSubmit,
  ref,
}: Props) => {
  const hasEvictorUpdatePermission = useAbility().can(
    'update',
    'ClusterAutoscaler'
  );
  const { cluster, isManaged } = useCluster();
  const initialValues = useMemo(
    () =>
      mapConfigToFormValues({
        config: evictorConfig,
        advancedConfig,
      }),
    [evictorConfig, advancedConfig]
  );

  const isFormEditable = isEvictorConfigurable({
    evictorConfig,
    clusterAgentVersion,
    hasEvictorUpdatePermission,
    clusterStatus: cluster.status,
    agentStatus: cluster.agentStatus,
    isClusterManaged: isManaged,
    isWoopInstalled,
  });

  return (
    <RffForm<FormValues>
      ref={ref}
      mode={isFormEditable ? 'edit' : 'view'}
      validate={validate}
      initialValues={initialValues}
      onSubmit={(formValues: FormValues) => {
        onSubmit(mapFormValuesToConfig(formValues));
      }}
    >
      {({ form }) => (
        <>
          <Layout testId="evictor-settings-content">
            <EvictorSettingsHeader
              startContent={
                <RffSwitch
                  name="enabled"
                  testId="evictor-enabled-switch"
                  size="md"
                  disabled={isPending}
                />
              }
            />
            <EvictorSettingsAlert
              evictorConfig={evictorConfig}
              clusterAgentVersion={clusterAgentVersion}
            />

            <Configuration />

            <AdvancedConfigurations />
          </Layout>

          <PageFormFooter>
            <Button
              testId="evictor-settings-cancel-button"
              variant="secondary"
              onClick={() => form.restart()}
            >
              Cancel
            </Button>
            <Button
              testId="evictor-settings-save-button"
              onClick={form.submit}
              loading={isPending}
            >
              Save changes
            </Button>
          </PageFormFooter>
        </>
      )}
    </RffForm>
  );
};

const EvictorSettingsFormSkeleton = () => {
  return (
    <Layout testId="evictor-settings-skeleton">
      <EvictorSettingsHeader />
      <Configuration.Skeleton />
      <AdvancedConfigurations.Skeleton />
    </Layout>
  );
};

type EvictorSettingsFormErrorProps = {
  refetch: () => void;
};

const EvictorSettingsFormError = ({
  refetch,
}: EvictorSettingsFormErrorProps) => {
  return (
    <Layout>
      <EvictorSettingsHeader />
      <FailedToLoad
        title="Failed to load bin-packing settings"
        refresh={refetch}
      />
    </Layout>
  );
};

EvictorSettingsForm.Skeleton = EvictorSettingsFormSkeleton;
EvictorSettingsForm.Error = EvictorSettingsFormError;
